/**
 * @file Per-level resource lifetime. Everything a level allocates on the GPU
 * goes through a Registry so `Level.dispose()` is a single call and switching
 * levels five times does not grow memory.
 *
 * Keys are namespaced by convention: `'tex:asphalt'`, `'mat:road'`,
 * `'geo:streetlight'`. The `texture()/material()/geometry()` helpers apply the
 * prefix for you, so `registry.material('road')` and `registry.get('mat:road')`
 * are the same slot.
 */

/**
 * @param {any} obj
 * @returns {boolean}
 */
const isDisposable = (obj) => !!obj && typeof obj.dispose === 'function';

export class Registry {
  /** @param {string} [label] Shown in warnings — usually the level id. */
  constructor(label = 'level') {
    this.label = label;
    /** @type {Map<string, any>} */
    this._map = new Map();
    /** @type {Set<any>} Anonymous disposables (render targets, workers…). */
    this._extra = new Set();
    this._disposed = false;
  }

  /**
   * Stores a value under `key`, returning it. Re-setting the same key disposes
   * the previous value first (a level should not leak by overwriting).
   * @template T
   * @param {string} key
   * @param {T} value
   * @returns {T}
   */
  set(key, value) {
    const prev = this._map.get(key);
    if (prev !== undefined && prev !== value && isDisposable(prev)) prev.dispose();
    this._map.set(key, value);
    return value;
  }

  /**
   * @param {string} key
   * @returns {any|undefined}
   */
  get(key) {
    return this._map.get(key);
  }

  /**
   * @param {string} key
   * @returns {boolean}
   */
  has(key) {
    return this._map.has(key);
  }

  /** @returns {string[]} */
  keys() {
    return [...this._map.keys()];
  }

  /**
   * Get-or-create a texture. `factory` runs at most once per key.
   * @param {import('../types.js').MaterialKey} key
   * @param {() => any} [factory]
   * @returns {any}
   */
  texture(key, factory) {
    return this._memo(`tex:${key}`, factory, 'texture');
  }

  /**
   * Get-or-create a material.
   * @param {import('../types.js').MaterialKey} key
   * @param {() => any} [factory]
   * @returns {any}
   */
  material(key, factory) {
    return this._memo(`mat:${key}`, factory, 'material');
  }

  /**
   * Get-or-create a shared geometry (instanced props, impostor quads…).
   * NOTE: extension beyond the T0 spec — merged level geometry is owned by the
   * scene graph and disposed by `Level.dispose()`, not by this.
   * @param {string} key
   * @param {() => any} [factory]
   * @returns {any}
   */
  geometry(key, factory) {
    return this._memo(`geo:${key}`, factory, 'geometry');
  }

  /**
   * Registers an anonymous disposable (render target, worker, atlas canvas).
   * @template T
   * @param {T} obj
   * @returns {T}
   */
  track(obj) {
    if (obj) this._extra.add(obj);
    return obj;
  }

  /**
   * Disposes everything, in reverse insertion order, and empties the registry.
   * Idempotent.
   */
  disposeAll() {
    if (this._disposed) return;
    this._disposed = true;

    const kill = (obj) => {
      try {
        if (isDisposable(obj)) obj.dispose();
        else if (typeof obj?.terminate === 'function') obj.terminate();
      } catch (err) {
        console.warn(`[registry:${this.label}] dispose failed`, err);
      }
    };

    for (const obj of [...this._extra].reverse()) kill(obj);
    for (const key of [...this._map.keys()].reverse()) kill(this._map.get(key));

    this._extra.clear();
    this._map.clear();
  }

  /**
   * @param {string} key
   * @param {(() => any)|undefined} factory
   * @param {string} what
   * @returns {any}
   */
  _memo(key, factory, what) {
    const hit = this._map.get(key);
    if (hit !== undefined) return hit;
    if (!factory) {
      console.warn(`[registry:${this.label}] missing ${what} "${key}" and no factory given`);
      return undefined;
    }
    return this.set(key, factory());
  }
}

/**
 * @param {string} [label]
 * @returns {Registry}
 */
export const createRegistry = (label) => new Registry(label);
